import { freelizer } from "freelizer";
import LogRocket from "logrocket";
import mixpanel from "mixpanel-browser";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import { AllAnts } from "./AllAnts";
import "./App.css";
import { FullCalendar } from "./FullCalendar";
import { HymnEditor } from "./HymnEditor";
import { LOHFAnts } from "./LOHFAnts";
import { LogoLink } from "./LogoLink";
import { NewHome } from "./NewHome";
import { NewNewHome } from "./NewNewHome";
import { OrdLitany } from "./OrdLitany";
import { Ordo } from "./Ordo";
import { PublicOrdo } from "./PublicOrdo";
import { AntiphonEditor } from "./antiphons/AntiphonEditor";
import { CalCheck } from "./calcheck/CalCheck";
import { SpecialExport } from "./calcheck/SpecialExport";
import { PSALTER_1662 } from "./chants/data-1662";
import { PSALMS_1928 } from "./chants/data-1928";
import { getTransposeByFromMode } from "./chants/eb-chant/note-utils";
import { getHalfEmphasisPoints } from "./chants/utils";
import { CanticlePage } from "./pages/canticle/CanticlePage";
import { CheckPage } from "./pages/check/CheckPage";
import { ComminationPage } from "./pages/commination/ComminationPage";
import { GuidePage } from "./pages/guide/GuidePage";
import { LitanyPage } from "./pages/litany/LitanyPage";
import { OrdinariateOfficeOfTheDead } from "./pages/pray/OrdinariateOfficeOfTheDead";
import { OrdinariatePage } from "./pages/pray/OrdinariatePage";
import { PrayPage } from "./pages/pray/PrayPage";
import { PsalmPage } from "./pages/psalm/PsalmPage";
import { RecordingPage } from "./pages/recording/RecordingPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { TagPage } from "./pages/tag/TagPage";
import { ComplineToday } from "./pages/today/ComplineToday";
import { EvensongToday } from "./pages/today/EvensongToday";
import { MattinsToday } from "./pages/today/MattinsToday";
import { YearPage } from "./pages/year/YearPage";
import { TenebraePage } from "./tenebrae/TenebraePage";
import {
    BACKGROUND_COLOR,
    HORIZONTAL_RULE_COLOR,
    PAGE_BACKGROUND,
    TEXT_COLOR,
    WIDTH,
} from "./tokens";
import { RemoteDataProvider } from "./utils/RemoteDataProvider";
import { useSettings } from "./utils/settings";

mixpanel.init("f5a4af976d34b678f65d2429cc9d3499");
LogRocket.init("4ptq78/singtheoffice");

const SmallScreenContext = createContext<boolean>(false);

export const useIsSmallScreen = () => {
    return useContext(SmallScreenContext);
};

const NULL: any = null;

const PopoverContext = createContext<{
    popoverId: string | undefined;
    setPopoverId: (id: string | undefined) => void;
}>(NULL);

export const usePopover = () => {
    return useContext(PopoverContext);
};

const ChantIndexContext = createContext<{
    getChantIndex: () => number;
}>(NULL);

export const useChantIndex = () => {
    return useContext(ChantIndexContext);
};

const w: any = window;

const startTuningAsync = async () => {
    console.log("starting tuner");
    const { start, subscribe } = await freelizer();
    start();
    subscribe(
        (
            tuningEvent:
                | { note: string | undefined; octave: number }
                | undefined
        ) => {
            w.tuningEvent = tuningEvent;
        }
    );
};

w.startTuning = async () => {
    if (w.isTuning) {
        // Already tuning
    } else {
        try {
            w.isTuning = true;
            await startTuningAsync();
        } catch {}
    }
};

function App() {
    if (!localStorage.getItem("region-switches")?.split(";;;")) {
        localStorage.setItem("region-switches", "");
    }

    const pageRef = useRef<HTMLDivElement>(null);

    const isSmallScreen = window.innerWidth <= 700;

    useEffect(() => {
        document.querySelector(".app-scroll-area")?.scrollTo(0, 0);

        if (window.location.hostname !== "localhost") {
            mixpanel.track("page-view", {
                path: window.location.href.split(".com")[1],
            });
        }
    }, []);

    const [popoverId, setPopoverId] = useState<string | undefined>(undefined);

    if (window.location.hostname === "localhost") {
        const mappedTextData = PSALMS_1928.map((psalm) => {
            return {
                ...psalm,
                lines: psalm.lines.map((line) => {
                    return line.map((half) => {
                        const emphasisPoints = getHalfEmphasisPoints(half);

                        const finalWords: string[] = [];
                        let syllableCount = 0;

                        half.split(" ")
                            .reverse()
                            .forEach((word) => {
                                const finalSyllables: string[] = [];

                                word.split("_")
                                    .reverse()
                                    .forEach((syllable) => {
                                        syllableCount++;

                                        if (
                                            emphasisPoints.includes(
                                                syllableCount
                                            )
                                        ) {
                                            finalSyllables.unshift(
                                                "/" + syllable
                                            );
                                        } else {
                                            finalSyllables.unshift(syllable);
                                        }
                                    });

                                finalWords.unshift(finalSyllables.join("_"));
                            });

                        return finalWords.join(" ");
                    });
                }),
            };
        });

        let diffs: any = [];
        for (let i = 1; i <= 150; i++) {
            const psalm1928 = PSALMS_1928.find(
                (psalm) => psalm.id === `psalm${i}`
            );
            const psalm1662 = PSALTER_1662.find(
                (psalm) => psalm.id === `psalm${i}`
            );

            if (psalm1928 && psalm1662) {
                for (
                    let li = 0;
                    li <
                    Math.max(psalm1928.lines.length, psalm1662.lines.length);
                    li++
                ) {
                    const line1928A = (psalm1928.lines[li]?.[0] ?? "")
                        .toLocaleString()
                        .toLocaleLowerCase()
                        .replaceAll(/[\,\.\'\!\?\:\;\-\_]/g, "");
                    const line1662A = (psalm1662.lines[li]?.[0] ?? "")
                        .toLocaleString()
                        .toLocaleLowerCase()
                        .replaceAll(/[\,\.\'\!\?\:\;\-\_]/g, "");

                    if (line1928A !== line1662A) {
                        diffs.push({
                            psalm: i,
                            line: li,
                            line1928A,
                            line1662A,
                        });
                    }

                    const line1928B = (psalm1928.lines[li]?.[1] ?? "")
                        .toLocaleString()
                        .toLocaleLowerCase()
                        .replaceAll(/[\,\.\'\!\?\:\;\-\_]/g, "");
                    const line1662B = (psalm1662.lines[li]?.[1] ?? "")
                        .toLocaleString()
                        .toLocaleLowerCase()
                        .replaceAll(/[\,\.\'\!\?\:\;\-\_]/g, "");

                    if (line1928B !== line1662B) {
                        diffs.push({
                            psalm: i,
                            line: li,
                            line1928B,
                            line1662B,
                        });
                    }
                }
            }
        }
    }

    const { settings, setSetting } = useSettings();
    const transposeByKey = "1";
    const transposeBy =
        settings.transpositions["1"] + getTransposeByFromMode(transposeByKey);
    const increaseTransposeBy = useCallback(() => {
        setSetting("transpositions", {
            ...settings.transpositions,
            ["1"]: transposeBy + 1,
        });
    }, [settings, transposeBy]);
    const decreaseTransposeBy = useCallback(() => {
        setSetting("transpositions", {
            ...settings.transpositions,
            ["1"]: transposeBy - 1,
        });
    }, [settings, transposeBy]);

    return (
        <RemoteDataProvider>
            <PopoverContext.Provider
                value={useMemo(() => {
                    return { popoverId, setPopoverId };
                }, [popoverId, setPopoverId])}
            >
                <BrowserRouter>
                    <Routes>
                        <Route
                            path="/antiphon-editor/:index"
                            element={<AntiphonEditor />}
                        />
                        <Route path="/hymn-editor" element={<HymnEditor />} />
                        <Route path="/calcheck" element={<CalCheck />} />
                        <Route
                            path="/special-export"
                            element={<SpecialExport />}
                        />
                        <Route
                            path="/*"
                            element={
                                <SmallScreenContext.Provider
                                    value={isSmallScreen}
                                >
                                    <div
                                        ref={pageRef}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "stretch",
                                            height: "100%",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <div
                                            style={{
                                                borderBottom: `1px solid ${HORIZONTAL_RULE_COLOR}`,
                                                boxShadow: `0 4px 4px -4px rgba(0, 0, 0, 0.1)`,
                                                backgroundColor:
                                                    BACKGROUND_COLOR,
                                                zIndex: 999,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    maxWidth: WIDTH,
                                                    width: "100%",
                                                    boxSizing: "border-box",
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    padding: isSmallScreen
                                                        ? 4
                                                        : 8,
                                                    paddingLeft: isSmallScreen
                                                        ? 4
                                                        : 0,
                                                    paddingRight: isSmallScreen
                                                        ? 4
                                                        : 0,
                                                    fontSize: "120%",
                                                }}
                                            >
                                                <LogoLink />
                                                <div
                                                    style={{ flexGrow: 1 }}
                                                ></div>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                        border: `1px solid #d9d9d9`,
                                                        borderRadius: 2,
                                                        height: 32,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            paddingLeft: 8,
                                                            paddingTop: 1,
                                                        }}
                                                    >
                                                        <svg
                                                            width="13"
                                                            height="14"
                                                            viewBox="0 0 13 14"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <ellipse
                                                                cx="10.5"
                                                                cy="10"
                                                                rx="2.5"
                                                                ry="2"
                                                                fill="black"
                                                            />
                                                            <ellipse
                                                                cx="2.5"
                                                                cy="12"
                                                                rx="2.5"
                                                                ry="2"
                                                                fill="black"
                                                            />
                                                            <line
                                                                x1="12.5"
                                                                y1="10"
                                                                x2="12.5"
                                                                y2="2"
                                                                stroke="black"
                                                            />
                                                            <line
                                                                x1="4.5"
                                                                y1="12"
                                                                x2="4.5"
                                                                y2="4"
                                                                stroke="black"
                                                            />
                                                            <path
                                                                d="M4 2L13 0V3L4 5V2Z"
                                                                fill="black"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div
                                                        style={{
                                                            fontSize: "85%",
                                                            paddingLeft: 4,
                                                            paddingRight: 8,
                                                            width: 24,
                                                        }}
                                                    >
                                                        {transposeBy >= 0
                                                            ? "+"
                                                            : ""}
                                                        {transposeBy}
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            width: 32,
                                                            borderLeft: `1px solid #d9d9d9`,
                                                            alignSelf:
                                                                "stretch",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={
                                                            decreaseTransposeBy
                                                        }
                                                    >
                                                        <svg
                                                            width="8"
                                                            height="6"
                                                            viewBox="0 0 8 6"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M3.99999 6L8 0H0L3.99999 6Z"
                                                                fill="black"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            width: 32,
                                                            borderLeft: `1px solid #d9d9d9`,
                                                            alignSelf:
                                                                "stretch",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={
                                                            increaseTransposeBy
                                                        }
                                                    >
                                                        <svg
                                                            width="8"
                                                            height="6"
                                                            viewBox="0 0 8 6"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M3.99999 0L8 6H0L3.99999 0Z"
                                                                fill="black"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "stretch",
                                                color: TEXT_COLOR,
                                                lineHeight: 1.5,
                                                overflow: "auto",
                                                flexGrow: 1,
                                                flexShrink: 1,
                                                backgroundColor:
                                                    PAGE_BACKGROUND,
                                            }}
                                            className="app-scroll-area"
                                        >
                                            <div
                                                style={{
                                                    paddingTop: isSmallScreen
                                                        ? 0
                                                        : 48,
                                                    paddingBottom: isSmallScreen
                                                        ? 0
                                                        : 64,
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        maxWidth: WIDTH,
                                                        width: "100%",
                                                        boxSizing: "border-box",
                                                        backgroundColor:
                                                            BACKGROUND_COLOR,
                                                        boxShadow:
                                                            "0 4px 8px rgba(0, 0, 0, 0.1)",
                                                        borderRadius:
                                                            isSmallScreen
                                                                ? 0
                                                                : 4,
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <Routes>
                                                        <Route
                                                            path="/year/:year"
                                                            element={
                                                                <YearPage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/pray/today/mattins"
                                                            element={
                                                                <MattinsToday />
                                                            }
                                                        />
                                                        <Route
                                                            path="/pray/today/evensong"
                                                            element={
                                                                <EvensongToday />
                                                            }
                                                        />
                                                        <Route
                                                            path="/pray/today/compline"
                                                            element={
                                                                <ComplineToday />
                                                            }
                                                        />
                                                        <Route
                                                            path="/pray/litany"
                                                            element={
                                                                <LitanyPage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/pray/commination"
                                                            element={
                                                                <ComminationPage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/pray/:year/:month/:date/:hour"
                                                            element={
                                                                <PrayPage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/tag/:psalm"
                                                            element={
                                                                <TagPage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/psalm/:psalm"
                                                            element={
                                                                <PsalmPage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/canticle/:id/:tone"
                                                            element={
                                                                <CanticlePage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/intro"
                                                            element={
                                                                <GuidePage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/settings"
                                                            element={
                                                                <SettingsPage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/check"
                                                            element={
                                                                <CheckPage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/recording-project"
                                                            element={
                                                                <RecordingPage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/se/litany"
                                                            element={
                                                                <OrdLitany />
                                                            }
                                                        />
                                                        <Route
                                                            path="/se/:year/:month/:date/:label/:hour"
                                                            element={
                                                                <OrdinariatePage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/se/office-of-the-dead/:hour"
                                                            element={
                                                                <OrdinariateOfficeOfTheDead />
                                                            }
                                                        />
                                                        <Route
                                                            path="/se/tenebrae/:day"
                                                            element={
                                                                <TenebraePage />
                                                            }
                                                        />
                                                        <Route
                                                            path="/secret-ordo"
                                                            element={<Ordo />}
                                                        />
                                                        <Route
                                                            path="/ordo"
                                                            element={
                                                                <PublicOrdo />
                                                            }
                                                        />
                                                        <Route
                                                            path="/first-edition"
                                                            element={
                                                                <NewHome />
                                                            }
                                                        />
                                                        <Route
                                                            path="/calendar"
                                                            element={
                                                                <FullCalendar />
                                                            }
                                                        />
                                                        <Route
                                                            path="/se/all-ants/:mode"
                                                            element={
                                                                <AllAnts />
                                                            }
                                                        />
                                                        <Route
                                                            path="/se/lohf-ants"
                                                            element={
                                                                <LOHFAnts />
                                                            }
                                                        />
                                                        <Route
                                                            path="/"
                                                            element={
                                                                <NewNewHome />
                                                            }
                                                        />
                                                    </Routes>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </SmallScreenContext.Provider>
                            }
                        />
                    </Routes>
                </BrowserRouter>
            </PopoverContext.Provider>
        </RemoteDataProvider>
    );
}

export default App;
